import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { tenantService } from '../../../services/tenantService';
import { industryService } from '../../../services/industryService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import IndustryConfetti from '../../../components/IndustryConfetti';
import * as LucideIcons from 'lucide-react';

import { AlertCircle, Eye, EyeOff, Loader2, CheckCircle, X, Sparkles, Zap, TrendingUp } from 'lucide-react';
import { PlanComparisonCard } from './PlanComparisonCard';


export const SignupForm = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [step, setStep] = useState(1); // 1: Company Info, 2: Plan Selection, 3: Account Info
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [subdomainCheck, setSubdomainCheck] = useState({
    checking: false,
    available: null,
    message: '',
    suggestions: []
  });
  const [formData, setFormData] = useState({
    // Company Info (Step 1)
    companyName: '',
    subdomain: '',
    tierId: '',
    industryId: '',
    // User Info (Step 2)
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });

  useEffect(() => {
    loadSubscriptionTiers();
    loadIndustries();
  }, []);

  // Debounced subdomain availability check
  useEffect(() => {
    if (!formData?.subdomain || formData?.subdomain?.length < 3) {
      setSubdomainCheck({
        checking: false,
        available: null,
        message: '',
        suggestions: []
      });
      setShowCelebration(false);
      return;
    }

    setSubdomainCheck(prev => ({ ...prev, checking: true }));
    // Don't hide celebration immediately - let it stay visible while checking

    const timeoutId = setTimeout(async () => {
      try {
        const result = await tenantService?.checkSubdomainAvailability(formData?.subdomain);
        setSubdomainCheck({
          checking: false,
          available: result?.available,
          message: result?.message,
          suggestions: result?.suggestions || []
        });
        
        // Trigger celebration if available
        if (result?.available === true) {
          setShowCelebration(true);
          // Auto-hide celebration after 10 seconds
          setTimeout(() => setShowCelebration(false), 10000);
        } else {
          // Only hide celebration if subdomain is NOT available
          setShowCelebration(false);
        }
      } catch (err) {
        console.error('Subdomain check error:', err);
        setSubdomainCheck({
          checking: false,
          available: null,
          message: 'Unable to check availability',
          suggestions: []
        });
        setShowCelebration(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData?.subdomain]);

  const loadSubscriptionTiers = async () => {
    try {
      const tiers = await tenantService?.getSubscriptionTiers();
      setSubscriptionTiers(tiers || []);
    } catch (err) {
      console.error('Failed to load subscription tiers:', err);
      setSubscriptionTiers([]);
    }
  };

  const loadIndustries = async () => {
    try {
      const data = await industryService?.getAllActive();
      
      // Deserialize confetti icons (convert icon names back to components)
      const industriesWithIcons = data?.map(industry => {
        if (industry?.confettiIcons && Array.isArray(industry?.confettiIcons)) {
          const deserializedIcons = industry?.confettiIcons?.map(iconData => {
            const IconComponent = LucideIcons?.[iconData?.iconName] || LucideIcons?.Circle;
            return {
              icon: IconComponent,
              color: iconData?.color,
              label: iconData?.label
            };
          });
          return { ...industry, confettiIcons: deserializedIcons };
        }
        return industry;
      });
      
      setIndustries(industriesWithIcons || []);
    } catch (err) {
      console.error('Failed to load industries:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Auto-generate subdomain from company name
    if (name === 'companyName') {
      const subdomain = value?.toLowerCase()?.replace(/[^a-z0-9]/g, '');
      setFormData((prev) => ({
        ...prev,
        subdomain: subdomain,
      }));
    }
    
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubdomainSuggestionClick = (suggestion) => {
    setFormData(prev => ({ ...prev, subdomain: suggestion }));
  };

  const handlePlanSelect = (tierId) => {
    setFormData(prev => ({ ...prev, tierId }));
    setShowCompareModal(false);
  };

  const validateStep1 = () => {
    if (!formData?.industryId) {
      setError('Please select your company industry');
      return false;
    }
    if (!formData?.companyName?.trim()) {
      setError('Company name is required');
      return false;
    }
    if (!formData?.subdomain?.trim()) {
      setError('Subdomain is required');
      return false;
    }
    if (subdomainCheck?.available === false) {
      setError('Please choose an available subdomain');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData?.tierId) {
      setError('Please select a subscription plan');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData?.firstName?.trim() || !formData?.lastName?.trim()) {
      setError('First and last name are required');
      return false;
    }
    if (!formData?.email?.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData?.password?.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
      setError(null);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
      setError(null);
    }
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      setStep(1);
    }
    setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateStep3()) {
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // Create tenant with industry
      const tenant = await tenantService?.createTenant({
        name: formData?.companyName,
        subdomain: formData?.subdomain,
        tierId: formData?.tierId,
        industryId: formData?.industryId
      });

      // Sign up user
      await signUp(
        formData?.email,
        formData?.password,
        {
          firstName: formData?.firstName,
          lastName: formData?.lastName,
          phone: formData?.phone,
          tenantId: tenant?.id
        }
      );

      navigate('/login');
    } catch (err) {
      setError(err?.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Industry-Specific Confetti Celebration */}
      {showCelebration && formData?.industryId && (
        <IndustryConfetti 
          naicsCode={industries?.find(ind => ind?.id === formData?.industryId)?.naicsCode}
          show={showCelebration}
          onComplete={() => setShowCelebration(false)}
        />
      )}
      
      {/* Wider Form Container with Dynamic Height */}
      <div className="max-w-signup mx-auto">
        <div className="bg-white rounded-xl shadow-xl overflow-visible transition-all duration-500 ease-in-out">
          {/* Header Section */}
          <div className="px-8 pt-8 pb-6 bg-gradient-to-r from-blue-600 to-purple-600">
            <h2 className="text-2xl font-bold text-white text-center">
              {step === 1 ? 'Create Your Workspace' : step === 2 ? 'Choose Your Plan' : 'Complete Your Account'}
            </h2>
            <p className="text-blue-100 text-center text-sm mt-2">
              {step === 1 ? 'Let\'s get your company set up' : step === 2 ? 'Select the perfect plan for your business' : 'Just a few more details'}
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="px-2 py-6 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                  step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {step > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
                </div>
                <span className={`ml-2 text-sm font-medium transition-colors duration-300 ${
                  step >= 1 ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  Company Info
                </span>
              </div>
              <div className="flex-1 h-1 mx-1 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 transition-all duration-500 ease-in-out"
                  style={{ width: step >= 2 ? '50%' : '0%' }}
                />
              </div>
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                  step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {step > 2 ? <CheckCircle className="w-5 h-5" /> : '2'}
                </div>
                <span className={`ml-2 text-sm font-medium transition-colors duration-300 ${
                  step >= 2 ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  Plan Selection
                </span>
              </div>
              <div className="flex-1 h-1 mx-1 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 transition-all duration-500 ease-in-out"
                  style={{ width: step >= 3 ? '100%' : '0%' }}
                />
              </div>
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                  step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  3
                </div>
                <span className={`ml-2 text-sm font-medium transition-colors duration-300 ${
                  step >= 3 ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  Account
                </span>
              </div>
            </div>
          </div>

          {/* Form Content with Dynamic Height and Minimal Padding */}
          <div className="px-8 py-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 animate-slideIn">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800">{error}</p>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(error)}
                      className="text-xs text-red-600 hover:text-red-700 underline mt-1"
                    >
                      Copy error details
                    </button>
                  </div>
                </div>
              )}

              {/* Step 1: Company Information */}
              {step === 1 && (
                <div className="space-y-4">
                  {/* Field 1: Industry/Niche - Always visible */}
                  <div className="transition-all duration-500 ease-in-out relative z-50">
                    <label htmlFor="industryId" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Industry/Niche *
                    </label>
                    <Select
                      id="industryId"
                      name="industryId"
                      value={formData?.industryId}
                      onChange={(value) => setFormData(prev => ({ ...prev, industryId: value }))}
                      options={industries?.map((industry) => ({
                        value: industry?.id,
                        label: industry?.name,
                        description: industry?.description
                      }))}
                      placeholder="Select your industry"
                      searchable
                      required
                      disabled={loading}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Choose the industry that best describes your business
                    </p>
                  </div>

                  {/* Field 2: Company Name - Revealed after industry selected */}
                  {formData?.industryId && (
                    <div className="transition-all duration-500 ease-in-out animate-slideIn">
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <Input
                        id="companyName"
                        name="companyName"
                        type="text"
                        value={formData?.companyName}
                        onChange={handleChange}
                        placeholder="Your Company Name"
                        required
                        disabled={loading}
                      />
                    </div>
                  )}

                  {/* Field 3: Subdomain - Revealed after industry selected (together with company name) */}
                  {formData?.industryId && (
                    <div className="transition-all duration-500 ease-in-out animate-slideIn">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700">
                            Subdomain *
                          </label>
                          {/* Gamified Call-to-Action */}
                          <div className="mt-1 flex items-center gap-2 text-xs">
                            <Zap className="w-3.5 h-3.5 text-amber-500" />
                            <span className="text-gray-600 font-medium">
                              Claim your unique domain before someone else does!
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Urgency Banner - Only show when not checking and no subdomain entered */}
                      {!formData?.subdomain && (
                        <div className="mb-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-blue-900">
                                🚀 Is your company name available?
                              </p>
                              <p className="text-xs text-blue-700 mt-0.5">
                                Check now and secure your branded workspace instantly!
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="relative">
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <Input
                              id="subdomain"
                              name="subdomain"
                              type="text"
                              value={formData?.subdomain}
                              onChange={handleChange}
                              placeholder="yourcompany"
                              required
                              disabled={loading}
                              className="w-full pr-10"
                            />
                            
                            {/* Availability Indicator - Inside input field */}
                            {formData?.subdomain?.length >= 3 && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
                                {subdomainCheck?.checking ? (
                                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                                ) : subdomainCheck?.available === true ? (
                                  <CheckCircle className="w-5 h-5 text-green-600 animate-pulse" />
                                ) : subdomainCheck?.available === false ? (
                                  <X className="w-5 h-5 text-red-600" />
                                ) : null}
                              </div>
                            )}
                          </div>
                          <span className="text-sm text-gray-500 whitespace-nowrap">.localprocrm.com</span>
                        </div>
                      </div>
                      
                      {/* Celebration Banner - Show when available */}
                      {showCelebration && subdomainCheck?.available === true && (
                        <div className="mt-3 p-4 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border-2 border-green-300 rounded-lg relative animate-slideIn">
                          <div className="relative flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                                <Sparkles className="w-5 h-5 text-white" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <p className="text-base font-bold text-green-900 flex items-center gap-2">
                                🎉 Congratulations! It's Available!
                              </p>
                              <p className="text-sm text-green-800 mt-1 font-medium">
                                <span className="font-bold">{formData?.subdomain}.localprocrm.com</span> is yours for the taking!
                              </p>
                              <p className="text-xs text-green-700 mt-1.5">
                                ✨ You're one step closer to launching your workspace. Keep going!
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Subdomain Status Message */}
                      {formData?.subdomain?.length >= 3 && subdomainCheck?.message && !showCelebration && (
                        <div className="mt-2">
                          <p className={`text-sm font-medium ${
                            subdomainCheck?.available === true 
                              ? 'text-green-600' 
                              : subdomainCheck?.available === false 
                              ? 'text-red-600' :'text-gray-500'
                          }`}>
                            {subdomainCheck?.message}
                          </p>
                          
                          {/* Alternative Suggestions */}
                          {subdomainCheck?.available === false && subdomainCheck?.suggestions?.length > 0 && (
                            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <div className="flex items-start gap-2 mb-2">
                                <Sparkles className="w-4 h-4 text-amber-600 mt-0.5" />
                                <p className="text-xs font-semibold text-amber-900">
                                  Don't worry! Here are some great alternatives:
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {subdomainCheck?.suggestions?.map((suggestion) => (
                                  <button
                                    key={suggestion}
                                    type="button"
                                    onClick={() => handleSubdomainSuggestionClick(suggestion)}
                                    className="px-3 py-1.5 text-xs font-medium bg-white text-amber-900 border border-amber-300 rounded-md hover:bg-amber-100 hover:border-amber-400 transition-all shadow-sm hover:shadow"
                                  >
                                    {suggestion}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <p className="mt-1 text-xs text-gray-500">
                        Only lowercase letters, numbers, and hyphens allowed
                      </p>
                    </div>
                  )}

                  {/* Continue Button - Only show when all step 1 fields completed */}
                  {formData?.industryId && formData?.companyName?.trim() && formData?.subdomain?.trim() && subdomainCheck?.available === true && (
                    <div className="transition-all duration-500 ease-in-out animate-slideIn pt-2">
                      <Button
                        type="button"
                        onClick={handleNext}
                        disabled={loading}
                        className="w-full"
                      >
                        Continue to Plan Selection
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Plan Selection */}
              {step === 2 && (
                <div className="space-y-4">
                  {/* Plan Selection Field */}
                  <div className="transition-all duration-500 ease-in-out animate-slideIn relative z-50">
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="tierId" className="block text-sm font-medium text-gray-700">
                        Select Plan *
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowCompareModal(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Compare Plans
                      </button>
                    </div>
                    <Select
                      id="tierId"
                      name="tierId"
                      value={formData?.tierId}
                      onChange={(value) => setFormData(prev => ({ ...prev, tierId: value }))}
                      options={(subscriptionTiers || [])?.map((tier) => ({
                        value: tier?.id,
                        label: `${tier?.name} - $${Number(tier?.base_price || 0) + Number(tier?.per_user_price || 0)}/${tier?.price_type === 'per_user' ? 'user/month' : 'month'}`,
                        description: tier?.features?.[0] || ''
                      }))}
                      placeholder="Choose a plan"
                      searchable
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      onClick={handleBack}
                      disabled={loading}
                      className="flex-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={handleNext}
                      disabled={loading || !formData?.tierId}
                      className="flex-1"
                    >
                      Continue to Account
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: User Information */}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        value={formData?.firstName}
                        onChange={handleChange}
                        placeholder="John"
                        required
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        value={formData?.lastName}
                        onChange={handleChange}
                        placeholder="Doe"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData?.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData?.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData?.password}
                        onChange={handleChange}
                        placeholder="Min. 8 characters"
                        required
                        disabled={loading}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password *
                    </label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData?.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter password"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={handleBack}
                      disabled={loading}
                      className="flex-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating Account...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Sign In Link */}
              <p className="text-center text-sm text-gray-600 pt-4 border-t border-gray-200">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                  disabled={loading}
                >
                  Sign in
                </button>
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Plan Comparison Modal */}
      {showCompareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Compare Plans</h2>
              <button
                onClick={() => setShowCompareModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <PlanComparisonCard
                onSelectPlan={handlePlanSelect}
                selectedTierId={formData?.tierId}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignupForm;